import { useState, useEffect } from 'react';
import { BarChart3, Users, Search, MousePointer, Target, TrendingUp, Car, Calendar, RefreshCw, Download, Filter, ChevronDown, ExternalLink, DollarSign, MapPin, Lock, Eye, EyeOff, Mail, CheckCircle, XCircle, Clock } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Check if already authenticated (session storage)
  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (auth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');

    try {
      await axios.post(`${API}/admin/login`, { password });
      setIsAuthenticated(true);
      sessionStorage.setItem('adminAuth', 'true');
    } catch (err) {
      setLoginError('Invalid password');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuth');
  };

  const [stats, setStats] = useState(null);
  const [searches, setSearches] = useState([]);
  const [clicks, setClicks] = useState([]);
  const [leads, setLeads] = useState([]);
  const [dbStats, setDbStats] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('7days');

  useEffect(() => {
    fetchAllData();
  }, [dateRange]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, searchesRes, clicksRes, leadsRes, scrapeRes, analyticsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/searches?limit=100`),
        axios.get(`${API}/admin/clicks?limit=100`),
        axios.get(`${API}/leads?limit=100`),
        axios.get(`${API}/scrape/status`),
        axios.get(`${API}/analytics/summary?days=30`).catch(() => ({ data: null }))
      ]);

      setStats(statsRes.data);
      setSearches(searchesRes.data.searches || []);
      setClicks(clicksRes.data.clicks || []);
      setLeads(leadsRes.data.leads || []);
      setDbStats(scrapeRes.data);
      setAnalyticsData(analyticsRes.data);
    } catch (e) {
      console.error('Failed to fetch admin data:', e);
    } finally {
      setLoading(false);
    }
  };

  const triggerScrape = async () => {
    try {
      await axios.post(`${API}/scrape/run`);
      alert('Scrape started! Check back in a few minutes.');
    } catch (e) {
      alert('Failed to start scrape');
    }
  };

  const exportData = (data, filename) => {
    const csv = convertToCSV(data);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const convertToCSV = (data) => {
    if (!data.length) return '';
    const headers = Object.keys(data[0]);
    const rows = data.map(row => headers.map(h => JSON.stringify(row[h] || '')).join(','));
    return [headers.join(','), ...rows].join('\n');
  };

  // Analytics calculations
  const getSearchAnalytics = () => {
    const makeCount = {};
    const modelCount = {};
    const priceRanges = { 'Under R200k': 0, 'R200k-R500k': 0, 'R500k-R1M': 0, 'Over R1M': 0 };
    const provinceCount = {};

    searches.forEach(s => {
      const filters = s.filters || {};
      if (filters.make) makeCount[filters.make] = (makeCount[filters.make] || 0) + 1;
      if (filters.model) modelCount[filters.model] = (modelCount[filters.model] || 0) + 1;
      if (filters.province) provinceCount[filters.province] = (provinceCount[filters.province] || 0) + 1;
      
      const maxPrice = filters.max_price || 0;
      if (maxPrice < 200000) priceRanges['Under R200k']++;
      else if (maxPrice < 500000) priceRanges['R200k-R500k']++;
      else if (maxPrice < 1000000) priceRanges['R500k-R1M']++;
      else priceRanges['Over R1M']++;
    });

    return {
      topMakes: Object.entries(makeCount).sort((a, b) => b[1] - a[1]).slice(0, 10),
      topModels: Object.entries(modelCount).sort((a, b) => b[1] - a[1]).slice(0, 10),
      priceRanges,
      provinces: Object.entries(provinceCount).sort((a, b) => b[1] - a[1])
    };
  };

  const getClickAnalytics = () => {
    const sourceCount = {};
    const actionCount = {};
    const referralCount = {};

    clicks.forEach(c => {
      if (c.source) sourceCount[c.source] = (sourceCount[c.source] || 0) + 1;
      if (c.action_type) actionCount[c.action_type] = (actionCount[c.action_type] || 0) + 1;
    });

    return {
      bySource: Object.entries(sourceCount).sort((a, b) => b[1] - a[1]),
      byAction: Object.entries(actionCount).sort((a, b) => b[1] - a[1])
    };
  };

  const analytics = {
    searches: getSearchAnalytics(),
    clicks: getClickAnalytics()
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Access</h1>
            <p className="text-gray-500 mt-2">Enter password to access the dashboard</p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg pr-12 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter admin password"
                  required
                  data-testid="admin-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {loginError && (
              <div className="bg-red-50 text-red-600 px-4 py-2 rounded-lg text-sm" data-testid="login-error">
                {loginError}
              </div>
            )}

            <button
              type="submit"
              disabled={loginLoading}
              className="w-full py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition disabled:opacity-50"
              data-testid="admin-login-button"
            >
              {loginLoading ? 'Verifying...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-500">AutoSearchSA Analytics & Tracking</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition flex items-center gap-2"
                data-testid="admin-logout-button"
              >
                <Lock className="w-4 h-4" />
                Logout
              </button>
              <button
                onClick={triggerScrape}
                className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Run Scraper
              </button>
              <button
                onClick={fetchAllData}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b">
        <div className="container">
          <div className="flex gap-1 overflow-x-auto">
            {['overview', 'traffic', 'searches', 'clicks', 'leads', 'database'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-3 font-medium capitalize transition border-b-2 ${
                  activeTab === tab
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="container py-8">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Search className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-gray-500 text-sm">Total Searches</span>
                </div>
                <div className="text-3xl font-bold text-gray-900">{stats?.totals?.searches || 0}</div>
                <div className="text-sm text-green-600">+{stats?.today?.searches || 0} today</div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <MousePointer className="w-5 h-5 text-green-600" />
                  </div>
                  <span className="text-gray-500 text-sm">Total Clicks</span>
                </div>
                <div className="text-3xl font-bold text-gray-900">{stats?.totals?.clicks || 0}</div>
                <div className="text-sm text-green-600">+{stats?.today?.clicks || 0} today</div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Target className="w-5 h-5 text-purple-600" />
                  </div>
                  <span className="text-gray-500 text-sm">Total Leads</span>
                </div>
                <div className="text-3xl font-bold text-gray-900">{stats?.totals?.leads || 0}</div>
                <div className="text-sm text-green-600">+{stats?.today?.leads || 0} today</div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Car className="w-5 h-5 text-amber-600" />
                  </div>
                  <span className="text-gray-500 text-sm">Cars in Database</span>
                </div>
                <div className="text-3xl font-bold text-gray-900">{stats?.totals?.cars_in_database || 0}</div>
              </div>
            </div>

            {/* Top Searches & Sources */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-bold text-gray-900 mb-4">Top Searched Makes</h3>
                <div className="space-y-3">
                  {analytics.searches.topMakes.slice(0, 8).map(([make, count], idx) => (
                    <div key={make} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded text-xs font-bold flex items-center justify-center">{idx + 1}</span>
                        <span className="font-medium text-gray-700">{make}</span>
                      </div>
                      <span className="text-gray-500">{count} searches</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-bold text-gray-900 mb-4">Clicks by Source</h3>
                <div className="space-y-3">
                  {analytics.clicks.bySource.slice(0, 8).map(([source, count], idx) => (
                    <div key={source} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 bg-green-100 text-green-600 rounded text-xs font-bold flex items-center justify-center">{idx + 1}</span>
                        <span className="font-medium text-gray-700">{source}</span>
                      </div>
                      <span className="text-gray-500">{count} clicks</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Price Range Distribution */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-4">Search Price Ranges</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(analytics.searches.priceRanges).map(([range, count]) => (
                  <div key={range} className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{count}</div>
                    <div className="text-sm text-gray-500">{range}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Traffic Tab - Comprehensive Analytics */}
        {activeTab === 'traffic' && (
          <div className="space-y-8">
            {/* Traffic Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-indigo-600" />
                  </div>
                  <span className="text-gray-500 text-sm">Page Views</span>
                </div>
                <div className="text-3xl font-bold text-gray-900">{analyticsData?.totals?.pageviews || 0}</div>
                <div className="text-sm text-green-600">+{analyticsData?.today?.pageviews || 0} today</div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-cyan-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-cyan-600" />
                  </div>
                  <span className="text-gray-500 text-sm">Sessions</span>
                </div>
                <div className="text-3xl font-bold text-gray-900">{analyticsData?.totals?.sessions || 0}</div>
                <div className="text-sm text-green-600">+{analyticsData?.today?.new_sessions || 0} today</div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <ExternalLink className="w-5 h-5 text-orange-600" />
                  </div>
                  <span className="text-gray-500 text-sm">External Clicks</span>
                </div>
                <div className="text-3xl font-bold text-gray-900">{analyticsData?.totals?.external_clicks || 0}</div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Search className="w-5 h-5 text-blue-600" />
                  </div>
                  <span className="text-gray-500 text-sm">Searches</span>
                </div>
                <div className="text-3xl font-bold text-gray-900">{analyticsData?.totals?.searches || 0}</div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Target className="w-5 h-5 text-purple-600" />
                  </div>
                  <span className="text-gray-500 text-sm">Leads</span>
                </div>
                <div className="text-3xl font-bold text-gray-900">{analyticsData?.totals?.leads || 0}</div>
                <div className="text-sm text-green-600">+{analyticsData?.today?.leads || 0} today</div>
              </div>
            </div>

            {/* Top Pages & External Clicks */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-bold text-gray-900 mb-4">Top Pages (Last 30 Days)</h3>
                <div className="space-y-3">
                  {analyticsData?.top_pages?.slice(0, 10).map((page, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 bg-indigo-100 text-indigo-600 rounded text-xs font-bold flex items-center justify-center">{idx + 1}</span>
                        <span className="font-medium text-gray-700 truncate max-w-xs">{page.path}</span>
                      </div>
                      <span className="text-gray-500">{page.views} views</span>
                    </div>
                  )) || <p className="text-gray-500">No page view data yet</p>}
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-bold text-gray-900 mb-4">Top External Clicks (Referrals)</h3>
                <div className="space-y-3">
                  {analyticsData?.top_external_clicks?.slice(0, 10).map((click, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 bg-orange-100 text-orange-600 rounded text-xs font-bold flex items-center justify-center">{idx + 1}</span>
                        <span className="font-medium text-gray-700 truncate max-w-xs" title={click.url}>{new URL(click.url).hostname}</span>
                      </div>
                      <span className="text-gray-500">{click.clicks} clicks</span>
                    </div>
                  )) || <p className="text-gray-500">No external click data yet</p>}
                </div>
              </div>
            </div>

            {/* Top Searches */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-4">Top Search Terms</h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {analyticsData?.top_searches?.slice(0, 10).map((search, idx) => (
                  <div key={idx} className="text-center p-3 bg-gray-50 rounded-lg">
                    <div className="text-lg font-bold text-blue-600">{search.count}</div>
                    <div className="text-sm text-gray-600 truncate">{search.term || 'Empty search'}</div>
                  </div>
                )) || <p className="text-gray-500 col-span-5">No search data yet</p>}
              </div>
            </div>

            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
              <h3 className="font-bold text-blue-900 mb-2">About Traffic Analytics</h3>
              <p className="text-blue-800 text-sm">
                Traffic data is collected automatically when users visit pages, click external links, and perform searches. 
                Session tracking uses anonymous session IDs stored in browser session storage. Data is stored in MongoDB 
                and can be exported for further analysis.
              </p>
            </div>
          </div>
        )}

        {/* Searches Tab */}
        {activeTab === 'searches' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Search History</h2>
              <button
                onClick={() => exportData(searches, 'searches')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Search Term</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Make</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Model</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Price Range</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Results</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {searches.slice(0, 50).map((s, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{s.search_terms || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{s.filters?.make || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{s.filters?.model || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {s.filters?.min_price || 0} - {s.filters?.max_price || 'Any'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{s.results_count || 0}</td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {new Date(s.timestamp).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Clicks Tab */}
        {activeTab === 'clicks' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Click & Referral Tracking</h2>
              <button
                onClick={() => exportData(clicks, 'clicks')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>

            {/* Click Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {analytics.clicks.byAction.slice(0, 4).map(([action, count]) => (
                <div key={action} className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="text-2xl font-bold text-blue-600">{count}</div>
                  <div className="text-sm text-gray-500 capitalize">{action.replace(/_/g, ' ')}</div>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Action</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Source</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Car ID</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Referral URL</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {clicks.slice(0, 50).map((c, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium capitalize">
                            {c.action_type?.replace(/_/g, ' ') || 'click'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{c.source || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono text-xs">{c.car_id || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 truncate max-w-xs">{c.referral_url || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {new Date(c.timestamp).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Leads Tab */}
        {activeTab === 'leads' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Lead Management</h2>
              <button
                onClick={() => exportData(leads, 'leads')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Phone</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Email Notification</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {leads.map((lead, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{lead.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{lead.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{lead.phone}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            lead.lead_type === 'inspection' ? 'bg-green-100 text-green-700' :
                            lead.lead_type === 'insurance' ? 'bg-purple-100 text-purple-700' :
                            lead.lead_type === 'finance' ? 'bg-blue-100 text-blue-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {lead.lead_type}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            lead.status === 'new' ? 'bg-amber-100 text-amber-700' :
                            lead.status === 'contacted' ? 'bg-blue-100 text-blue-700' :
                            'bg-green-100 text-green-700'
                          }`}>
                            {lead.status}
                          </span>
                        </td>
                        <td className="px-4 py-3" data-testid={`lead-email-status-${idx}`}>
                          {lead.email_status === 'sent' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700" title={`Sent at ${lead.email_sent_at ? new Date(lead.email_sent_at).toLocaleString() : ''}`}>
                              <CheckCircle className="w-3 h-3" /> Sent
                            </span>
                          ) : lead.email_status === 'failed' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-700" title={lead.email_error || 'Email sending failed'}>
                              <XCircle className="w-3 h-3" /> Failed
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-500">
                              <Clock className="w-3 h-3" /> Pending
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400">
                          {new Date(lead.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Database Tab */}
        {activeTab === 'database' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Database & Scraper Status</h2>
              <button
                onClick={triggerScrape}
                className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Run Scraper Now
              </button>
            </div>

            {/* DB Stats */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-4">Cars in Database by Source</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {dbStats?.database?.by_source?.map((item, idx) => (
                  <div key={idx} className="text-center p-4 bg-gray-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{item.count}</div>
                    <div className="text-sm text-gray-500">{item.source}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <div className="text-center">
                  <span className="text-sm text-gray-500">Total Cars:</span>
                  <span className="text-2xl font-bold text-blue-600 ml-2">{dbStats?.database?.total_listings || 0}</span>
                </div>
              </div>
            </div>

            {/* Last Scrape Info */}
            {dbStats?.last_scrape && (
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <h3 className="font-bold text-gray-900 mb-4">Last Scrape</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <span className="text-sm text-gray-500">Time:</span>
                    <p className="font-medium">{new Date(dbStats.last_scrape.timestamp).toLocaleString()}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Cars Scraped:</span>
                    <p className="font-medium">{dbStats.last_scrape.total_cars}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">Status:</span>
                    <p className="font-medium text-green-600">{dbStats.last_scrape.status}</p>
                  </div>
                </div>
                {dbStats.last_scrape.sources && (
                  <div className="mt-4">
                    <span className="text-sm text-gray-500">Sources:</span>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {dbStats.last_scrape.sources.map((s, idx) => (
                        <span key={idx} className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">{s}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Available Sources */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <h3 className="font-bold text-gray-900 mb-4">Available Scraper Sources</h3>
              <div className="flex flex-wrap gap-2">
                {dbStats?.available_sources?.map((source, idx) => (
                  <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">{source}</span>
                ))}
              </div>
            </div>

            {/* Scraping Schedule */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-bold text-gray-900 mb-2">Automatic Scraping Schedule</h3>
              <p className="text-gray-600">
                The database is automatically updated every day at <strong>2:00 AM SAST</strong>. 
                You can also manually trigger a scrape using the button above.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
